import { Button } from "@carbon/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";
import { object, string } from "zod";

import { ConfirmActionModal } from "src/components/Modal";
import { UsersFilterableSinglePickerFormField } from "src/components/Pickers/User/UserFilterableSinglePickerFormField";
import { useToast } from "src/components/Toasts/ToastContainer";
import { mapToModalError } from "src/errors";
import { useAssignAlertMutation } from "src/graphql/anti-fraud/queries/alerts/mutations/AssignAlertMutation";
import { useT } from "src/internationalization";
import { useCurrentUser } from "src/providers/CurrentUserProvider";
import { Alert } from "src/types/alert.ts";
import { User } from "src/types/user";

export type AssignAlertModalProps = {
  alert: Alert;
  onSubmit?: () => void;
  onClose?: () => void;
};

type FormState = {
  user: User;
};

export const AssignAlertModal: FC<AssignAlertModalProps> = ({ alert, onSubmit, onClose }) => {
  const validationSchema = useValidationSchema();

  const {
    control,
    formState: { errors },
    handleSubmit: onFormSubmit,
    setValue,
  } = useForm<FormState>({
    resolver: zodResolver(validationSchema),
  });

  const { currentUser } = useCurrentUser();

  const {
    performMutation: assignAlert,
    error,
    loading,
  } = useAssignAlertMutation({
    onSuccess: () => {
      showSuccessToast(t("assign.alert.success.message"));
      onSubmit?.();
    },
  });

  const { showSuccessToast } = useToast();
  const t = useT();

  const handleSubmit = async ({ user }: FormState) => {
    await assignAlert({
      id: alert.id,
      userId: user.id,
    });
  };

  return (
    <ConfirmActionModal
      title={t("assign.alert.modal.title")}
      loading={loading}
      onConfirm={onFormSubmit(handleSubmit)}
      onClose={onClose}
      size="md"
      error={mapToModalError(error)}
      subtitle={t("page.alert.title", { number: alert?.alertNumber })}
      confirmButtonText={t("general.actions.confirm")}
      initialFocus="#user"
    >
      <div className="flex items-center gap-5">
        <UsersFilterableSinglePickerFormField
          id="user"
          control={control}
          errors={errors}
          label={t("assign.alert.picker.label")}
          name="user"
          helperText={t("assign.alert.picker.helper.text")}
        />
        {alert.assignee?.id !== currentUser.id && (
          <Button kind="ghost" onClick={() => setValue("user", currentUser)}>
            {t("general.actions.assign.to.me")}
          </Button>
        )}
      </div>
    </ConfirmActionModal>
  );
};

const useValidationSchema = () => {
  const t = useT();

  return useMemo(
    () =>
      object({
        user: object(
          {
            id: string(),
            name: string(),
            email: string(),
            firstName: string(),
            lastName: string(),
          },
          {
            message: t("assign.alert.picker.required.error"),
          }
        ),
      }),
    [t]
  );
};
